import { type HTMLAttributes } from 'react';

import { cn } from '../lib/cn.js';
import { severity, type Severity } from '../lib/severity.js';

interface SeverityBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  level: Severity;
  /** Render the level as a tinted pill instead of a solid colored badge. */
  subtle?: boolean;
  /** Render the bare colored dot without text (compact list contexts). */
  dotOnly?: boolean;
}

/** Renders a severity level using the canonical color mapping (DESIGN_SYSTEM §9). */
export function SeverityBadge({
  level,
  subtle,
  dotOnly,
  className,
  children,
  ...props
}: SeverityBadgeProps) {
  const tokens = severity(level);
  if (dotOnly) {
    return (
      <span
        aria-label={tokens.label}
        className={cn('inline-block h-2 w-2 rounded-full', tokens.bg, className)}
        {...props}
      />
    );
  }
  if (subtle) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold',
          tokens.subtle,
          tokens.ink,
          className,
        )}
        {...props}
      >
        <span className={cn('h-1.5 w-1.5 rounded-full', tokens.bg)} aria-hidden />
        {children ?? tokens.label}
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold',
        tokens.bg,
        tokens.fg,
        className,
      )}
      {...props}
    >
      {children ?? tokens.label}
    </span>
  );
}
